import { reportTypes, serializedTypes } from '@/reports/types';
import {
  VehicleReportVehicleSpecs,
  AutomobileOption,
  Automobile,
  AutomobileRecall,
} from '@/reports/types/vehicles';
import { createGuid } from '@/utils/createGuid';
import {
  detailedAccidentsHistory,
  detailedRecallsHistory,
  detailedSalesHistory,
  detailedSalvageHistory,
  detailedTheftsHistory,
  addIdsToSections,
} from './vehicleSectionSerializer';
import isBlacklisted from '../../utils/isBlacklisted';

export function SerializeVehicleReport(rawVehicleReport: reportTypes.Report) {
  let is_Blacklisted = false;

  const vinParam =
    rawVehicleReport?.meta?.request_info?.query_parameters.filter(
      (e) => e.name === 'vin'
    );
  const vin = vinParam ? vinParam[0]?.values[0] : '';

  if (
    rawVehicleReport.meta?.report_info &&
    rawVehicleReport.meta?.report_info.proxy_flags
  ) {
    is_Blacklisted = isBlacklisted(
      rawVehicleReport.meta?.report_info.proxy_flags
    );
  }

  const automobiles = rawVehicleReport.entities?.vehicles.automobiles;

  if (automobiles && Array.isArray(automobiles)) {
    automobiles.forEach((automobile, index) => {
      const openRecalls =
        automobile.recalls?.filter(
          (recall: AutomobileRecall) => recall.status?.toLowerCase() === 'open'
        ) || [];
      const closedRecalls =
        automobile.recalls?.filter(
          (recall: AutomobileRecall) =>
            recall.status?.toLowerCase() === 'closed'
        ) || [];
      const otherRecalls =
        automobile.recalls?.filter(
          (recall: AutomobileRecall) =>
            !(recall.status?.toLowerCase() === 'open')
        ) || [];
      const origin =
        automobile.origin?.city || automobile.origin?.country ? 1 : 0;

      const specificationsCount = () => {
        const mainKeys = [
          'make',
          'displayOrigin',
          'category',
          'type',
          'year',
          'model',
          'class',
          'size',
        ];
        const specsKeys = [
          'steering_type',
          'gross_weight',
          'highway_mileage',
          'driven_wheels',
          'anti_brake_system',
          'curb_weight',
          'city_mileage',
        ];
        const specsEngineKeys = [
          'fuel_capacity',
          'fuel_type',
          'engine',
          'size',
        ];
        const specsTransmissionKeys = ['gears', 'type'];
        const pricesKeys = ['msrp', 'delivery_charges', 'invoice_price'];
        const designKeys = [
          'trim',
          'length',
          'width',
          'standard_seating',
          'style',
          'trim_variation',
          'height',
          'doors',
          'optional_seating',
        ];

        const mainReducer = (count: number, key: string) =>
          ((automobile as Automobile)[key] ? count + 1 : count) as number;
        const specsReducer = (count: number, key: string) =>
          (automobile as Automobile).specs?.[key] ? count + 1 : count;
        const pricesReducer = (count: number, key: string) =>
          (automobile as Automobile).prices?.[key] ? count + 1 : count;
        const designReducer = (count: number, key: string) =>
          (automobile as Automobile).design?.[key] ? count + 1 : count;
        const specsEngineReducer = (count: number, key: string) =>
          (automobile as Automobile).specs?.engine?.[key] ? count + 1 : count;
        const specsTransmissionReducer = (count: number, key: string) =>
          (automobile as Automobile).specs?.transmission?.[key]
            ? count + 1
            : count;

        return (
          mainKeys.reduce(mainReducer, 0) +
          specsKeys.reduce(specsReducer, 0) +
          pricesKeys.reduce(pricesReducer, 0) +
          designKeys.reduce(designReducer, 0) +
          specsEngineKeys.reduce(specsEngineReducer, 0) +
          specsTransmissionKeys.reduce(specsTransmissionReducer, 0) +
          origin
        );
      };
      const repairsCount = automobile.repairs?.length || 0;
      const inspectionCount = automobile.inspection_events.length || 0;
      const maintenanceCount =
        automobile.maintenances?.filter(
          (maintenance) =>
            maintenance.intervals && maintenance.intervals.length > 0
        )?.length || 0;

      const equipmentCount = () => {
        const equipments = automobile.equipments.length || 0;
        const colors = automobile.design?.colors.length || 0;
        return equipments + colors;
      };

      const valuationCount = () => {
        const { valuation } = automobile;
        const keys = [
          'clean_trade',
          'average_trade',
          'rough_trade',
          'clean_retail',
          'period',
        ];
        return keys.filter((key) => Boolean(valuation?.[key])).length;
      };

      const pricesCount = () => {
        const { prices } = automobile;
        const keys = ['average', 'lowest', 'highest'];
        return keys.filter((key) => Boolean(prices?.[key])).length;
      };

      automobile.id = createGuid();
      addIdsToSections(automobile);
      automobile.locked_sections = rawVehicleReport.locked_sections;
      automobile.jsonPath = `$.entities.vehicles.automobiles[${index}]`;
      automobile.detailedHistory = generateDetailedHistory(automobile);
      automobile.vehicleYMM = generateVehicleYMM(automobile);
      automobile.auctionsDamage =
        automobile.auctions?.filter((auction) => {
          return (
            auction.vehicle_condition_details &&
            auction.vehicle_condition_details.damage_description !== null
          );
        }) || [];
      automobile.openRecalls = openRecalls;
      automobile.closedRecalls = closedRecalls;
      automobile.otherRecalls = otherRecalls;

      automobile.valuationCount = valuationCount();
      automobile.pricesCount = pricesCount();
      automobile.marketValueCount = automobile.vin
        ? automobile.valuationCount
        : automobile.pricesCount;
      automobile.costsCount = automobile.ownership_costs.costs.length;
      automobile.maintenanceCount = maintenanceCount;
      automobile.equipmentCount = equipmentCount();
      automobile.warrantiesCount = automobile.warranties.length;
      automobile.openRecallsCount = openRecalls.length;
      automobile.closedRecallsCount = closedRecalls.length;
      automobile.otherRecallsCount = otherRecalls.length;
      automobile.recallsCount = openRecalls.length + otherRecalls.length;
      automobile.recallsWithStatusCount =
        openRecalls.length + closedRecalls.length;
      automobile.accidentsCount =
        automobile.damage_history?.accidents.length ||
        automobile.accidents.length;
      automobile.salesCount = automobile.sales.length;
      automobile.auctionsCount = automobile.auctions.length;
      automobile.listingsCount =
        automobile.salesCount + automobile.auctionsCount;
      automobile.salvagesCount =
        automobile.damage_history?.salvage.length || automobile.salvage.length;
      automobile.theftsCount = automobile.thefts.length;
      automobile.titleRegistrationsCount =
        automobile.title_registrations.length;
      automobile.titleChecksCount = automobile.title_checks.length;
      automobile.exportsCount = automobile.exports.length;
      automobile.impoundsCount = automobile.impounds.length;
      automobile.liensCount = automobile.liens.length;
      automobile.ownershipHistoryCount = automobile.ownership_history.length;
      automobile.titleRecordsCount =
        automobile.titleChecksCount +
        automobile.titleRegistrationsCount +
        automobile.exportsCount +
        automobile.impoundsCount +
        automobile.liensCount +
        automobile.ownershipHistoryCount;
      automobile.damageHistoryCount =
        automobile.accidentsCount +
        automobile.salvagesCount +
        automobile.auctionsDamage.length +
        (automobile.damage_history?.repair_quotes.length || 0);
      automobile.detailedHistoryCount = automobile.detailedHistory.length;
      automobile.safetyRatingsCount = automobile.safety_ratings.length;
      automobile.repairsCount = repairsCount;
      automobile.inspectionEventsCount = inspectionCount;
      automobile.imagesCount = automobile.images.length;
      automobile.specificationsCount = specificationsCount();
      automobile.specsCount =
        automobile.specificationsCount + automobile.equipmentCount;
      automobile.servicesAndRepairsCount =
        repairsCount + inspectionCount + maintenanceCount;
      automobile.isClassicVin = Boolean(automobile.is_classic_vin);
      automobile.vinDecodeConfidenceScore =
        automobile.vin_decode_confidence_score as number;
    });
  }

  const serializedVehicleMeta = {
    ...rawVehicleReport.meta,
    query_name: vin,
    metaName: generateMetaName(rawVehicleReport.meta),
  };

  const serializedVehicleReport = {
    rawData: rawVehicleReport,
    id: rawVehicleReport.meta?.permalink,
    meta: serializedVehicleMeta,
    alert_me: rawVehicleReport.meta?.alert_me || false,
    created_at: rawVehicleReport.meta?.created_at,
    updated_at: rawVehicleReport.meta?.updated_at,
    report_upgraded: rawVehicleReport.meta?.report_upgraded,
    rating: rawVehicleReport.meta?.rating,
    isBlacklisted: is_Blacklisted,
    automobiles,
  } as serializedTypes.SerializedVehicleReport;

  return serializedVehicleReport;
}

const generateDetailedHistory = (automobilePayload: Automobile) => {
  return [
    detailedAccidentsHistory(
      automobilePayload.damage_history?.accidents || automobilePayload.accidents
    ),
    detailedRecallsHistory(
      automobilePayload.recalls,
      automobilePayload.jsonPath
    ),
    detailedSalesHistory(automobilePayload.sales),
    detailedSalvageHistory(
      automobilePayload.damage_history?.salvage || automobilePayload.salvage
    ),
    detailedTheftsHistory(automobilePayload.thefts),
  ].flat(1);
};

export function generateVehicleSpecs(
  serializedVehicleReport: serializedTypes.SerializedVehicleReport
) {
  const automobile =
    serializedVehicleReport.rawData.entities?.vehicles.automobiles[0];
  const installedOptions: AutomobileOption[] = [];
  const availableOptions: AutomobileOption[] = [];
  automobile?.options.map((option) => {
    (option.installed ? installedOptions : availableOptions).push(option);
  });

  const vehicleReportVehicleSpecs = {
    general: {
      ymm: [automobile?.year, automobile?.make, automobile?.model]
        .filter((item) => Boolean(item))
        .join(' '),
      type: automobile?.type,
      origin: [automobile?.origin?.city, automobile?.origin?.country]
        .filter((el) => Boolean(el))
        .join(', '),
    },
    price: {
      msrp: automobile?.prices.msrp,
      invoicePrice: automobile?.prices.invoice_price,
      deliveryCharges: automobile?.prices.delivery_charges,
    },
    fuel: {
      type: automobile?.specs?.engine?.fuel_type,
      capacity: automobile?.specs?.engine?.fuel_capacity,
      highwayMileage: automobile?.specs?.highway_mileage,
      cityMileage: automobile?.specs?.city_mileage,
    },
    details: {
      category: automobile?.category,
      size: automobile?.size,
      style: automobile?.design?.style,
      engine: automobile?.specs?.engine?.engine,
      engineSize: automobile?.specs?.engine?.size,
      trim: automobile?.design?.trim,
      steeringType: automobile?.specs?.steering_type,
      antiBrakeSystem: automobile?.specs?.anti_brake_system,
      length: automobile?.design?.length,
      width: automobile?.design?.width,
      height: automobile?.design?.height,
      doors: automobile?.design?.doors,
      standardSeating: automobile?.design?.standard_seating,
      transmissionType: automobile?.specs?.transmission?.type,
      drivenWheels: automobile?.specs?.driven_wheels,
    },
    installedOptions,
    availableOptions,
    equipments: automobile?.equipments,
  } as VehicleReportVehicleSpecs;

  return vehicleReportVehicleSpecs;
}

const generateVehicleYMM = (automobilePayload: Automobile) => {
  return [
    automobilePayload?.year,
    automobilePayload?.make,
    automobilePayload?.model,
  ]
    .filter((item) => Boolean(item))
    .join(' ')
    .toUpperCase();
};

export const optionsBySearchType = [
  {
    type: 'vin',
    fields: ['vin'],
    joinBy: ' ',
  },
  {
    type: 'plate',
    fields: ['license_plate', 'state'],
    joinBy: ' - ',
  },
  {
    type: 'ymm',
    fields: ['year', 'make', 'model', 'trim'],
    joinBy: ' ',
  },
];

const generateMetaName = (meta: reportTypes.Meta | null | undefined) => {
  const requestInfo = meta?.request_info?.query_parameters;
  let metaName = '';
  if (Array.isArray(requestInfo)) {
    const emtpy = { fields: [], joinBy: ' ' };
    const valuesForSearchBy =
      optionsBySearchType.find((item) => item.type === meta?.search_by) ||
      emtpy;

    const values = valuesForSearchBy.fields.map((f: string) => {
      const field = requestInfo.find(
        (info: reportTypes.QueryParameter) => info.name === f
      );
      return field ? field.values[0] : null;
    });
    metaName =
      values
        .filter((item) => Boolean(item))
        .join(valuesForSearchBy?.joinBy)
        .toUpperCase() || '';
  }

  return metaName;
};
